import React from 'react';
import { number, string } from 'prop-types';
import { formatPrice } from '../util/prices';

/**
 * We get product data both from the deprecated service (product-listings) and
 * from gen-products. There are several differences in the data, some of which
 * are normalized at the gql layer, and some of which are done here.
 *
 * Normalized in gql:
 * - product has retailer (normalized into retailerName)
 * - productTeaser/productEmbed has retailerName
 *
 * - product has imageUrl (raw image URL pasted into product-listings)
 * - productTeaser/productEmbed has image (full image metadata from gen-image),
 *   which includes image.url (normalized into imageUrl)
 *
 * Normalized by this component:
 * - product has currentPrice (string; always used) and originalPrice (used
 *   when currentPrice represents a discount).
 * - productTeaser/productEmbed has price (always used) and salePrice (used to
 *   represent a discount).
 */
export default WrappedComponent => {
  function withCompositeProduct(props) {
    const { currentPrice, originalPrice, price, salePrice, ...rest } = props;

    const hasSalePrice =
      (originalPrice && currentPrice) || (price && salePrice);

    // products
    let productPrice = (price && formatPrice(price)) || undefined;
    let productSalePrice =
      (hasSalePrice && formatPrice(salePrice)) || undefined;

    // legacy products
    if (!price && (currentPrice || originalPrice)) {
      productPrice = hasSalePrice ? originalPrice : currentPrice;
      productSalePrice = hasSalePrice ? currentPrice : undefined;
    }

    return (
      <WrappedComponent
        {...rest}
        price={productPrice}
        salePrice={productSalePrice}
      />
    );
  }

  withCompositeProduct.propTypes = {
    currentPrice: string,
    originalPrice: string,
    price: number,
    salePrice: number
  };

  withCompositeProduct.defaultProps = {
    currentPrice: undefined,
    originalPrice: undefined,
    price: undefined,
    salePrice: undefined
  };

  return withCompositeProduct;
};
